import { useState } from "react";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import Modal from "../../../components/base/Modal";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import TextBox from "../../../components/form/TextBox";
import { API_ROUTES } from "../../../core/Constants";
import { updateUsersOn, useHub } from "../../../core/Global";
import User from "../models/User";


interface IProps {
    user: User
    onClose: () => void
}

// Root only. Deletes the whole account: numbers, PBX extensions, tenant database and every
// user under it. The button stays disabled until the account's username is typed exactly;
// the HUB checks the same text again before it deletes anything.
export default function CustomersDeleteAccountModal(props: IProps) {

    const hub = useHub();
    const [typed, typedSet] = useState<string>("");

    const matches = !!props.user.username && typed.trim().toLowerCase() == props.user.username.trim().toLowerCase();

    return (
        <Modal id="customers-delete-account-modal"
            title="Delete Account"
            titleIcon="trash"
            open visible staticBackdrop
            onClose={props.onClose}
            buttonText="Delete Account Permanently"
            buttonColor="danger"
            confirmDisabled={!matches}
            onConfirm={callback => {
                hub.Post(API_ROUTES.Customers + "deleteaccount", {
                    id: props.user.id,
                    confirmUsername: typed.trim()
                }, res => {
                    if (res.success) {
                        toastSuccess(`'${props.user.username}' has been deleted.`);
                        if (res.data?.movedSystemAdmins?.length) toastSuccess("System admins moved: " + res.data.movedSystemAdmins.join(", "));
                        // Things left alone on purpose or not removed from the PBX; also in the deletion log.
                        if (res.data?.skipped?.length) toastError("Not removed: " + res.data.skipped.join("; "));
                        updateUsersOn();
                        callback();
                    }
                    else {
                        toastError(res.message ?? "Couldn't delete the account.");
                        callback(true);
                    }
                }, () => {
                    toastError("Couldn't reach the server. Check the deletion log before trying again.");
                    callback(true);
                })
            }}>
            <div>
                <FlexRow className="mb-2">
                    <Icon icon="user" type="solid" className="me-4" size={18} />
                    <span className="fs-3 mt-1">{props.user.fullName}</span>
                </FlexRow>
                <FlexRow className="mb-8">
                    <Icon icon="at" type="solid" className="me-4" size={18} />
                    <span className="fs-3 mt-1">{props.user.username}</span>
                </FlexRow>

                <div className="alert alert-danger mb-6">
                    <div className="fw-bold mb-2">This permanently deletes the whole account and can't be undone:</div>
                    <ul className="mb-0 ps-5">
                        <li>All of its phone numbers are released on Twilio and removed from the PBX</li>
                        <li>Its extensions and queues are removed from the PBX</li>
                        <li>Its database (jobs, contacts, calls, chats, reports...) is dropped</li>
                        <li>The owner and every admin, dispatcher and technician under it are deleted, with their sessions, wallet, plan and payment methods</li>
                    </ul>
                    <div className="mt-2">Invoices and payments are kept for accounting. System admins working in this account are never deleted; they are moved to another account.</div>
                </div>

                <TextBox id="txt-delete-account-confirm" name="confirmUsername" labelUp block autoFocus
                    label={`Type the username "${props.user.username}" to confirm`}
                    value={typed}
                    onTextChanges={text => typedSet(text)} />
            </div>
        </Modal>
    )
}
